// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Whether the row lists notices.
 *
 * - `recorded` — at least one linked notice names the data subject.
 *   At least one of them is published, approved or a draft, and not
 *   `replaced` by a newer revision (O-17).
 * - `no_current_notice` — linked notices name it, and every one of them is
 *   withdrawn, superseded, or replaced by a newer revision that no longer
 *   names it (product-qa P3-5, gate E12.5 pass 3; O-17): the notices
 *   are listed, and the row is not read as covered. Exact within the bound,
 *   because every read keeps the notices that are not withdrawn or superseded
 *   first (`notice-coverage-service.ts`).
 * - `not_recorded` — none does (UC-VN-113 failure mode 2: *Not recorded*).
 * - `not_visible` — the reader does not hold `privacy_notice:read`, so the
 *   notice register is not read for them at all: `notices` is empty and the
 *   row says nothing about whether a notice exists (Sprint 62.58.0 PR 3, D2).
 */
export const NOTICE_COVERAGE_STATES = [
    'recorded',
    'no_current_notice',
    'not_recorded',
    'not_visible',
];
/**
 * The most linked notices of one kind (`data_subject_group` naming the data
 * subjects asked about, or `public`) one read lists for an activity, in the
 * order published, approved, draft, withdrawn, superseded, the most recently
 * drafted first within each (gate E12.5 pass 3, P3-2). Reaching it sets
 * `truncated`; it never decides a row's state.
 */
export const NOTICE_COVERAGE_NOTICE_LIMIT = 500;
// ── The deployment access statement (AC-N3) ─────────────────────────
/** Where the statements live. Every document carries `org_id` first. */
export const ACCESS_STATEMENTS_COLLECTION = 'grc_access_statements';
/** What a role or position may do with the data — closed and instrument-neutral. */
export const ACCESS_STATEMENT_ACCESS_KINDS = [
    'view',
    'extract',
    'export',
    'delete',
    'configure',
];
/** The bounds AC-N3 states, and the ones the body schema enforces. */
export const ACCESS_STATEMENT_LIMITS = {
    role_or_position: 200,
    means: 500,
    conditions: 1000,
    /** Entries in one revision. */
    entries: 100,
    /** Data-map systems one revision may name. */
    data_map_system_ids: 100,
};
/** The error codes the access statement answers with. */
export const ACCESS_STATEMENT_ERRORS = {
    /** A statement id of another organisation, or none (404). */
    notFound: 'ACCESS_STATEMENT_NOT_FOUND',
    /**
     * The revision named as replaced is not the current one, or another revision
     * won the race to replace it (409). Nothing is written.
     */
    versionConflict: 'ACCESS_STATEMENT_VERSION_CONFLICT',
    /** A named system is not one of this organisation's (400, `field_errors`). */
    systemNotFound: 'ACCESS_STATEMENT_SYSTEM_NOT_FOUND',
    /**
     * Gate E12.5 pass 1 (P3-1) — the revision changes the data-map systems
     * (names one the revision it replaces does not, or drops one) and the caller
     * does not hold `dm:system:read`, the grant the systems are read under (403,
     * `required_permission`). Checked before any system is read, so the caller
     * learns nothing about which system ids exist. Carrying the replaced
     * revision's systems over unchanged needs no system grant.
     */
    systemGrantRequired: 'ACCESS_STATEMENT_SYSTEM_GRANT_REQUIRED',
    /** The named approver is not a user of this organisation (400, `field_errors`). */
    approverNotFound: 'ACCESS_STATEMENT_APPROVER_NOT_FOUND',
    /** `reviewed_on` is after the organisation's today (422, `field_errors`). */
    reviewedOnInvalid: 'ACCESS_STATEMENT_REVIEWED_ON_INVALID',
};
//# sourceMappingURL=deployment-privacy.js.map